import ArrowForward from '@material-ui/icons/ArrowForward';
import { Link } from '@backstage/core-components';
import {
  CountPill,
  SectionIntro,
  useProductPaths,
  useRecentProducts,
} from '@univerus/plugin-products';
import { useHomeStyles } from './homeStyles';
import { recentDocPath } from './recentDocs';
import { useRecentDocs } from './useRecentDocs';

export function RecentlyViewed() {
  const classes = useHomeStyles();
  const paths = useProductPaths();
  // The signed-in user's own history only (see recentProducts.ts and
  // recentDocs.ts), products and docs interleaved by when they were viewed.
  const products = useRecentProducts();
  // Undefined until the docs have their catalog titles; the list waits for
  // them so cards do not reorder as they arrive.
  const docs = useRecentDocs();
  const items = [
    ...products.map(product => ({ type: 'product' as const, product })),
    ...(docs ?? []).map(doc => ({ type: 'doc' as const, doc })),
  ]
    .sort(
      (a, b) =>
        (b.type === 'product' ? b.product : b.doc).viewedAt -
        (a.type === 'product' ? a.product : a.doc).viewedAt,
    )
    .slice(0, 4);
  return (
    <section
      className={classes.recentSection}
      aria-labelledby="recently-viewed-title"
    >
      {/* No browse link: the quick links above already lead to Products and
          the Knowledge Base, and either alone would mislabel a mixed list. */}
      <div className={classes.sectionHead}>
        <SectionIntro
          size="section"
          id="recently-viewed-title"
          title="Recently viewed"
        />
      </div>
      {docs && items.length > 0 && (
        <div className={classes.recent}>
          {items.map(item =>
            item.type === 'product' ? (
              <Link
                key={`product:${item.product.id}`}
                className={classes.recentLink}
                to={paths.product(item.product.id)}
              >
                <span className={classes.cardEyebrow}>
                  {item.product.portfolio}
                </span>
                <strong>{item.product.name}</strong>
                {item.product.tagline && (
                  <span className={classes.recentDescription}>
                    {item.product.tagline}
                  </span>
                )}
                <span className={classes.cardFooter}>
                  {/* The count is information, like on the Products page; the
                      arrow is the card's one call to action. */}
                  {item.product.offeringCount === undefined ? (
                    <span />
                  ) : (
                    <CountPill>
                      {item.product.offeringCount}{' '}
                      {item.product.offeringCount === 1
                        ? 'offering'
                        : 'offerings'}
                    </CountPill>
                  )}
                  <ArrowForward aria-hidden data-arrow />
                </span>
              </Link>
            ) : (
              <Link
                key={`doc:${recentDocPath(item.doc)}`}
                className={classes.recentLink}
                to={recentDocPath(item.doc)}
              >
                <span className={classes.cardEyebrow}>Documentation</span>
                <strong>{item.doc.title}</strong>
                {item.doc.description && (
                  <span className={classes.recentDescription}>
                    {item.doc.description}
                  </span>
                )}
                <span className={classes.cardFooter}>
                  <span />
                  <ArrowForward aria-hidden data-arrow />
                </span>
              </Link>
            ),
          )}
        </div>
      )}
      {docs && items.length === 0 && (
        <div className={classes.empty}>
          Products and documentation you view will appear here as you explore.
        </div>
      )}
      <nav className={classes.onboarding} aria-label="New here">
        <strong>New here?</strong>
        <Link to="/docs/default/system/univerus-hub-system/onboarding/">
          Getting started
        </Link>
        <Link to="/docs/default/component/company-reference">
          Company reference
        </Link>
      </nav>
    </section>
  );
}
